import React from 'react';

/**
 * Coerces an unknown value to a non-empty string, or `undefined`.
 * Useful for reading string fields off loosely-typed payloads (e.g. serialized
 * request params) where the value may be missing, empty, or another type.
 *
 * @example
 * asString('usd') // 'usd'
 * asString('') // undefined
 * asString(42) // undefined
 */
export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Capitalizes the first letter of a string and lowercases the rest.
 *
 * @param str - The string to capitalize
 * @returns The capitalized string
 *
 * @example
 * capitalize('hello') // 'Hello'
 * capitalize('HELLO') // 'Hello'
 */
export function capitalize<T extends string>(str: T): Capitalize<Lowercase<T>> {
  return (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()) as Capitalize<Lowercase<T>>;
}

/**
 * Capitalizes the first letter of a string while preserving the rest.
 *
 * @example
 * capitalizeFirst('hello') // 'Hello'
 * capitalizeFirst('API key') // 'API key'
 * capitalizeFirst('') // ''
 */
export function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

/**
 * Converts a snake_case or space-separated string to Title Case.
 *
 * @example
 * titleCase('review_required') // 'Review Required'
 * titleCase('hello world') // 'Hello World'
 */
export function titleCase(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Extracts a string from a ReactNode for accessibility purposes.
 * Useful for aria-label attributes when the label might be a React element.
 *
 * @param node - The ReactNode to extract text from
 * @returns The extracted string, or empty string if no text can be extracted
 */
export function getAriaLabel(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);

  // For React elements, try to extract text content
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode };
    if (props.children) {
      return getAriaLabel(props.children);
    }
  }

  // For arrays, join all text content
  if (Array.isArray(node)) {
    return node.map((child) => getAriaLabel(child)).join('');
  }

  return '';
}

/**
 * Strips common markdown formatting from a string, returning plain text.
 * Handles images, links, inline code, bold/italic, strikethrough, headings,
 * blockquotes, and list markers.
 *
 * @param text - The markdown string to strip
 * @returns The plain text string
 *
 * @example
 * stripMarkdown('**Hello** [world](https://example.com)') // 'Hello world'
 * stripMarkdown('# Heading') // 'Heading'
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/!\[.*?\]\(.*?\)/g, '') // images
    .replace(/\[([^\]]*)\]\(.*?\)/g, '$1') // links
    .replace(/(`{1,3})(.*?)\1/g, '$2') // inline code
    .replace(/\*{1,3}(.*?)\*{1,3}/g, '$1') // bold/italic with asterisks
    .replace(/_{1,3}(.*?)_{1,3}/g, '$1') // bold/italic with underscores
    .replace(/~~(.*?)~~/g, '$1') // strikethrough
    .replace(/^#{1,6}\s+/gm, '') // headings
    .replace(/^[>\-*+]\s+/gm, '') // blockquotes/lists
    .replace(/^\d+\.\s+/gm, '') // ordered lists
    .trim();
}

// eslint-disable-next-line no-control-regex
const HIDDEN_CHARS_REGEX = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F\xAD\u0600-\u0605\u061C\u06DD\u070F\u0890\u0891\u08E2\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2069\u206A-\u206F\uFEFF\uFFF9-\uFFFB]/g;

/**
 * Strips invisible/hidden Unicode characters from a string.
 * Removes zero-width spaces, directional overrides, soft hyphens, and other
 * non-printable format characters that can cause issues in text processing.
 * Preserves normal whitespace (spaces, tabs, newlines).
 */
export function stripHiddenCharacters(str: string): string {
  return str.replace(HIDDEN_CHARS_REGEX, '');
}

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
};

/**
 * Escapes special characters in a string for use in HTML.
 * Prevents XSS attacks when inserting untrusted strings into HTML.
 *
 * @param str - The string to escape
 * @returns The escaped string
 *
 * @example
 * escapeHtml('<script>alert(1)</script>') // '&lt;script&gt;alert(1)&lt;/script&gt;'
 */
export function escapeHtml(str: string): string {
  if (!str) return '';

  return str.replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char]);
}

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
const DEFAULT_HEX_COLOR = '#000000';

/**
 * Sanitizes a hex color value to prevent XSS when used in HTML style attributes.
 * Returns the color if it's a valid hex format, otherwise returns a safe default.
 *
 * @param color - The color string to sanitize
 * @param fallback - Optional fallback color (defaults to #000000)
 * @returns A valid hex color string
 *
 * @example
 * sanitizeHexColor('#ff0000') // '#ff0000'
 * sanitizeHexColor('#F00') // '#F00'
 * sanitizeHexColor('invalid"><script>') // '#000000'
 * sanitizeHexColor('', '#ffffff') // '#ffffff'
 */
export function sanitizeHexColor(color: string, fallback: string = DEFAULT_HEX_COLOR): string {
  if (!color) return fallback;

  return HEX_COLOR_REGEX.test(color) ? color : fallback;
}

/**
 * Converts bracket notation array indices to dot notation.
 * Useful for normalizing API field paths to match React Hook Form's path format.
 *
 * @param path - The path string with potential bracket notation
 * @returns The path with bracket indices converted to dot notation
 *
 * @example
 * bracketToDotNotation('items[0].name') // 'items.0.name'
 * bracketToDotNotation('users[2].addresses[0].street') // 'users.2.addresses.0.street'
 */
export function bracketToDotNotation(path: string): string {
  return path.replace(/\[(\d+)\]/g, '.$1');
}
